// ds-typo-ignore-file — mockup simulates report viewer UI, not marketing site DS
"use client"

import { useState, useRef, useCallback } from "react"
import { m, AnimatePresence } from "framer-motion"
import { ReportMockupNav } from "./report-mockup-nav"
import { ReportMockupSidebar } from "./report-mockup-sidebar"
import { ReportMockupCards } from "./report-mockup-cards"

export type ExplainerKey =
  | "selector"
  | "bioAge"
  | "heartRisk"
  | "biomarkers"
  | "findings"
  | "summary"
  | "sidebarSummary"
  | "nav"

const TIP_W = 280 // matches max-w-[280px]
const TIP_H = 100 // generous estimate for ~3 lines
const PAD = 8

/**
 * iPad-framed report mockup with optional explainer tooltips.
 * Pass `explainers` to enable hover/touch tooltips keyed to `data-explainer` zones
 * on the sub-components (ReportMockupNav, ReportMockupSidebar, ReportMockupCards).
 *
 * Positioning is computed in one pass from the cursor position at zone entry,
 * using TIP_W as the assumed width. The tooltip then stays still until the
 * cursor enters a different zone — no post-render measurement, no reflow.
 */
export function ReportMockupIpad({
  explainers,
  hideSidebar = false,
  compact = false,
  screenMaxHeight,
  contentScale,
  frameAspect,
  bloodOnly = false,
}: {
  explainers?: Partial<Record<ExplainerKey, string>>
  hideSidebar?: boolean
  /** Thinner bezel padding + rounding, for use in tight comparison rows. */
  compact?: boolean
  /** Optional max-height on the inner screen (clips content; bezel stays intact). */
  screenMaxHeight?: string
  /** Scale factor applied to the inner screen content (e.g. 0.8). Bezel size is unaffected. */
  contentScale?: number
  /** Aspect ratio for the iPad frame (e.g. "4/3" for landscape iPad). Forces the bezel to a fixed shape. */
  frameAspect?: string
  /** Blood-only page: the summary card shows the blood sample line, not the MRI one. */
  bloodOnly?: boolean
}) {
  const containerRef = useRef<HTMLDivElement>(null)
  const [tooltip, setTooltip] = useState<{
    key: ExplainerKey
    text: string
    left: number
    top: number
  } | null>(null)
  const hideTimerRef = useRef<ReturnType<typeof setTimeout> | null>(null)

  const hasTooltips = explainers && Object.keys(explainers).length > 0

  const clearHideTimer = () => {
    if (hideTimerRef.current) {
      clearTimeout(hideTimerRef.current)
      hideTimerRef.current = null
    }
  }

  const handlePointer = useCallback(
    (e: React.MouseEvent | React.TouchEvent) => {
      if (!explainers) return
      const clientX = "touches" in e ? (e.touches[0]?.clientX ?? 0) : e.clientX
      const clientY = "touches" in e ? (e.touches[0]?.clientY ?? 0) : e.clientY
      const target = document
        .elementFromPoint(clientX, clientY)
        ?.closest("[data-explainer]") as HTMLElement | null
      if (!target) {
        if (tooltip) {
          clearHideTimer()
          hideTimerRef.current = setTimeout(() => setTooltip(null), 400)
        }
        return
      }
      clearHideTimer()
      const key = target.dataset.explainer as ExplainerKey
      const text = explainers[key]
      if (!text) {
        setTooltip(null)
        return
      }
      // Only reposition when entering a new zone. Inside the same zone the
      // tooltip stays put — prevents the flicker the user was seeing.
      if (tooltip?.key === key) return

      const box = containerRef.current!.getBoundingClientRect()
      const cx = clientX - box.left
      const cy = clientY - box.top
      const flipX = cx + 16 + TIP_W > box.width - PAD
      const flipY = cy + TIP_H > box.height - PAD
      let left = flipX ? cx - 16 - TIP_W : cx + 16
      let top = flipY ? cy + 8 - TIP_H : cy - 8
      if (left + TIP_W > box.width - PAD) left = box.width - TIP_W - PAD
      if (left < PAD) left = PAD
      if (top + TIP_H > box.height - PAD) top = box.height - TIP_H - PAD
      if (top < PAD) top = PAD
      setTooltip({ key, text, left, top })
    },
    [explainers, tooltip]
  )

  return (
    <div
      ref={containerRef}
      className="relative mx-auto"
      onMouseMove={hasTooltips ? handlePointer : undefined}
      onTouchStart={hasTooltips ? handlePointer : undefined}
      onMouseLeave={
        hasTooltips
          ? () => {
              clearHideTimer()
              hideTimerRef.current = setTimeout(() => setTooltip(null), 600)
            }
          : undefined
      }
      onTouchEnd={
        hasTooltips
          ? () => {
              clearHideTimer()
              hideTimerRef.current = setTimeout(() => setTooltip(null), 5000)
            }
          : undefined
      }
    >
      {/* iPad frame — even bezels on all four sides, subtle edge highlight, layered drop shadow */}
      <div
        className={[
          compact
            ? "relative flex flex-col rounded-[20px] p-1.5 sm:rounded-[24px] sm:p-2 md:rounded-[28px] md:p-2.5"
            : "relative flex flex-col rounded-[28px] p-3 sm:rounded-[36px] sm:p-4 md:rounded-[44px] md:p-5",
          frameAspect ? "w-full" : "",
        ]
          .filter(Boolean)
          .join(" ")}
        style={{
          // Vertical gradient suggests the curved metal edge catching light
          background:
            "linear-gradient(180deg, #1e1e1e 0%, #0a0a0a 50%, #1e1e1e 100%)",
          boxShadow: [
            // Inner rim: faint highlight at the top, darker line at the bottom
            "inset 0 1px 0 rgba(255,255,255,0.08)",
            "inset 0 -1px 0 rgba(0,0,0,0.5)",
            // Layered drop shadow for depth on light surfaces
            "0 1px 2px rgba(0,0,0,0.05)",
            "0 10px 30px -6px rgba(0,0,0,0.14)",
            "0 40px 80px -24px rgba(0,0,0,0.22)",
          ].join(", "),
          ...(frameAspect ? { aspectRatio: frameAspect } : {}),
        }}
      >
        {/* Front camera dot — centered in top bezel */}
        <div
          aria-hidden="true"
          className="pointer-events-none absolute top-1.5 left-1/2 h-1.5 w-1.5 -translate-x-1/2 rounded-full bg-black sm:top-2 sm:h-2 sm:w-2 md:top-2.5"
          style={{
            boxShadow:
              "0 0 0 1px rgba(255,255,255,0.06), inset 0 0 2px rgba(255,255,255,0.15)",
          }}
        />
        {/* Screen — rounded to match the bezel minus padding, subtle inner edge */}
        <div
          className={[
            compact
              ? "relative flex flex-col overflow-hidden rounded-[14px] bg-white sm:rounded-[18px] md:rounded-[20px]"
              : "relative flex flex-col overflow-hidden rounded-[16px] bg-white sm:rounded-[22px] md:rounded-[26px]",
            frameAspect ? "min-h-0 flex-1" : "",
          ]
            .filter(Boolean)
            .join(" ")}
          style={{
            boxShadow: "inset 0 0 0 1px rgba(255,255,255,0.04)",
            ...(screenMaxHeight && !frameAspect
              ? { maxHeight: screenMaxHeight }
              : {}),
          }}
        >
          <div
            style={
              contentScale && contentScale !== 1
                ? {
                    transform: `scale(${contentScale})`,
                    transformOrigin: "top left",
                    width: `${100 / contentScale}%`,
                  }
                : undefined
            }
          >
            <div data-explainer="nav">
              <ReportMockupNav />
            </div>
            <div className="flex">
              {!hideSidebar && (
                <div
                  className="hidden shrink-0 p-2 lg:block"
                  style={{ width: "28%" }}
                >
                  <ReportMockupSidebar />
                </div>
              )}
              <div className="flex min-w-0 flex-1 flex-col p-2 md:p-3">
                <ReportMockupCards bloodOnly={bloodOnly} />
              </div>
            </div>
          </div>
        </div>
      </div>

      {/* safe-animate-presence: conditional-mount — gated by `tooltip` truthy */}
      <AnimatePresence>
        {tooltip && (
          <m.div
            key={tooltip.key}
            initial={{ opacity: 0, scale: 0.95 }}
            animate={{ opacity: 1, scale: 1 }}
            exit={{ opacity: 0, scale: 0.95 }}
            transition={{ duration: 0.15, ease: [0.16, 1, 0.3, 1] }}
            className="pointer-events-none absolute z-50 w-[280px] rounded-2xl border border-white/40 bg-white/80 px-4 py-2 text-sm font-medium text-neutral-700 shadow-lg backdrop-blur-md"
            style={{ left: tooltip.left, top: tooltip.top }}
          >
            {tooltip.text}
          </m.div>
        )}
      </AnimatePresence>
    </div>
  )
}
